"use client";
import React, { useState } from "react";
import FuelTypeBox from "@components/atoms/FuelTypeBox";
import styles from "./PriceByContentSection.module.scss";
import MileageModal from "../MileageModal";
import { formatToLakhs } from "@utils/numberFormatter";
import { formatDeliveryDate } from "@utils/formatDate";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { CarData } from "@components/organisms/VariantContentBlock/VariantContentBlock.types";

interface PriceByContentSectionClientWrapperProps {
  title: string;
  items: CarData[];
  isSellable: boolean;
  isLoggedIn: boolean;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
}

export default function PriceByContentSectionClientWrapper({
  title,
  items,
  isSellable,
  isLoggedIn,
  pageType,
  brandName,
  modelName,
  carAvailability,
}: PriceByContentSectionClientWrapperProps) {
  const { name: cityName } = getSelectedCityClient();
  const [modalData, setModalData] = useState<{
    type: string;
    count: number;
  } | null>(null);

  const getMatchingVariants = (type: string) =>
    items?.filter(
      (variant) => variant.key.toLowerCase() === type.toLowerCase()
    )[0].variants || [];

  function transformCarDataForMileageModal(rawData: any[]) {
    return rawData.map((item) => ({
      name: item.variant.name,
      fuelType: item.fuelType,
      transmission: item.transmission,
      mileage: item?.mileage?.value,
      formattedPrice: formatToLakhs(item.minColorPrice),
      expressDelivery: isSellable ? item.expressDeliveryFlag : false,
      expressDeliveryDate: isSellable
        ? formatDeliveryDate(item.minDeliveryDays)
        : "",
      standardDelivery: isSellable
        ? formatDeliveryDate(item.minStandardDeliveryDays)
        : "",
      slug: item.slug,
      status: item?.status,
    }));
  }

  const handleFuelTypeBoxClick = async (item: CarData) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_choose_a_variant",
        section: "Price page summary",
        selected_value: getTitle(title, item.key),
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        city_name: cityName || "",
      },
    });

    setModalData({
      type: item.key,
      count: item.variants.length,
    });
  };

  return (
    <>
      <div className={styles.list}>
        {items.map((item, index) => (
          <FuelTypeBox
            key={index}
            fuelType={`${getTitle(title, item.key)}`}
            minPrice={item.minValue}
            maxPrice={item.maxValue}
            variants={item.variants.length}
            onClick={() => handleFuelTypeBoxClick(item)}
          />
        ))}
      </div>

      <MileageModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        title={
          modalData
            ? `Available in ${modalData.count} variant${
                modalData.count > 1 ? "s" : ""
              }`
            : "Available in number of variants"
        }
        variants={
          modalData
            ? transformCarDataForMileageModal(
                getMatchingVariants(modalData.type)
              )
            : []
        }
        preventScoll={true}
        isSellable={isSellable}
        isLoggedIn={isLoggedIn}
        segmentPageType={pageType}
        segmentActionName="tap_btn_variant"
        segmentSection="Price page summary"
        cityName={cityName}
        brandName={brandName}
        modelName={modelName}
      />
    </>
  );
}

function getTitle(title: string, value: string) {
  switch (title) {
    case "Seating Capacity":
      return `${value} Seater`;
    case "Engine Type":
      return `${value} cc`;
    case "Certified Range":
      return `${Math.round(Number(value))} kms`;
    case "Battery Capacity":
      return `${Number(value)/1000} kWh`;
    default:
      return value;
  }
}
